import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_05: GgxTourDefinition = {
    key: '[HOWTO-05] Using your own data in Evals',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 4,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Running Evaluations on Your Own Data',
        subtitle:
            'Earlier tours ran evaluations on the sample data the platform provides. To measure ' +
            'risk against your own context, you can supply your own data instead. This tour ' +
            'reuses a completed run and swaps in custom data before submitting the job.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You reused a completed run, chose to upload custom data, checked the columns the ' +
            'report needs, filled in the template, and submitted the job. Compare the results ' +
            'against the base run to see how your own data scores.',
        learned: [
            'A completed run can be **reused** with your own data.',
            'The report **tells you the columns** it needs; the template matches them.',
            '**Compare against the base run** to see how your data scores.',
        ],
    },
    chapters: [
        {
            name: 'Open the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'map',
            steps: [2, 3, 4],
        },
        {
            name: 'Find a base run',
            blurb: 'Open Jobs and pick a completed run',
            icon: 'clock',
            steps: [5, 6],
        },
        {
            name: 'Reuse and customize',
            blurb: 'Rerun it with your own data',
            icon: 'pulse',
            steps: [7, 8],
        },
        {
            name: 'Provide your data',
            blurb: 'Match the columns and fill the template',
            icon: 'list',
            steps: [9, 10],
        },
        {
            name: 'Run the job',
            blurb: 'Submit and compare',
            icon: 'compass',
            steps: [11],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: "Let's open the pipeline we will evaluate",
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will use <strong>Combined Customer Intent and Response Pipeline</strong>, ' +
                'which already has a completed evaluation we can reuse. ' +
                'The quickest way to it is the global search. It finds pipelines, prompts, ' +
                'reports and portals by name from any page, and <strong>Ctrl+K</strong> opens ' +
                'it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens, with its tabs across the top.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            target: '[test-id="tab-item-Jobs"]',
            scrollIntoView: true,
            title: 'Reuse a run instead of starting over',
            whatHappens: "the Jobs tab opens with the pipeline's past runs.",
            icon: 'clock',
            description:
                'Setting up an evaluation from scratch means picking a report, data and settings ' +
                'all over again. It is quicker to take a finished run and change only the data. ' +
                'Jobs keeps every past run of this pipeline, so that is where we look. Click ' +
                '<strong>Jobs</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            actionLabel: 'Jobs',
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="pinned-job-Example Dashboard Run"]',
            title: 'This finished run is a good base',
            whatHappens: "the run's dashboard opens with its scores and settings.",
            icon: 'grid',
            description:
                'We need a run that completed successfully, because it already has a working ' +
                'report and settings attached. <strong>Example Dashboard Run</strong> is pinned ' +
                'at the top and complete. Click it.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="rerun-button"]',
            title: 'Rerun lets us change just the data',
            whatHappens: 'a new job form opens, pre-filled from this run.',
            icon: 'pulse',
            description:
                'Rerun copies this job so you can adjust parts of it and run it again. By ' +
                'default it repeats everything exactly, which is useful on its own, but today we ' +
                'will swap the sample data for yours. Click <strong>Rerun</strong>.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="eval-data-options-item-Upload Custom Data"]',
            title: 'Time to bring your own data',
            whatHappens: 'the sample dataset is replaced by an empty template you can fill in.',
            icon: 'search',
            description:
                'Sample data shows whether the pipeline works in general. Your own data shows ' +
                'whether it works for your customers, your products and your wording, which is ' +
                'what matters for risk. Each evaluation lets you choose where its data comes ' +
                'from. Click <strong>Upload Custom Data</strong> for this one.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
        {
            target: '[test-id="expected-columns"]',
            title: 'These are the columns your data needs',
            whatHappens: 'next you add your rows to the template.',
            icon: 'list',
            description:
                'A report can only score what it can read, so your file needs the right columns. ' +
                'Green columns are the ones the pipeline itself uses, such as the question. The ' +
                'other color marks columns the report adds, like the expected answer. Darker ' +
                'names are required, lighter ones are optional. Hover over any column name to ' +
                'see what goes in it, then click Continue.',
            side: 'bottom',
            align: 'start',
        },
        {
            target: '[test-id="eval-data0"]',
            title: 'Add your rows',
            whatHappens: 'your rows become the input for this evaluation.',
            icon: 'grid',
            description:
                'You can upload a CSV or Excel file with those columns, or type a few rows ' +
                'straight into the template. Even five or ten real examples will tell you a lot. ' +
                'Just make sure every required column has a value in each row, then click ' +
                'Continue.',
            side: 'top',
            align: 'start',
        },
        {
            target: '[test-id="run-new-job"]',
            title: "Your data is in. Let's run it",
            whatHappens: 'the job starts running in the background.',
            icon: 'compass',
            description:
                'GGX will send each of your rows through the pipeline, compare the replies ' +
                'against your expected answers and score them with the report. Small files ' +
                'finish in about 5 to 10 minutes, and you can follow progress in the Jobs tab. ' +
                'When it is done, compare the scores against the base run. Click <strong>Run New ' +
                'Job</strong>.',
            side: 'left',
            align: 'center',
            buttons: ['previous', 'close'],
            advanceOnTargetClick: true,
        },
    ],
};
